/**
 * Future native app CastingProvider — FOUNDATION + REQUIRES NATIVE APP.
 *
 * Abstraction slot for iOS / Android / tvOS / Android TV shells that will
 * bridge into CastingManager. Same contract as other providers; not available
 * until a native module registers itself (e.g. window.__TPG_NATIVE_CAST__).
 */

import { createEmitter } from "../../Utilities/events";
import {
  CastingCapabilityState,
  CastingConnectionState,
} from "./CastingEvents";
import { createReceiverRequiredProvider } from "./createReceiverRequiredProvider";
import { CastPlatformReceiverRequirements } from "./CastReceiverContract";

function detectNativeBridge() {
  if (typeof window === "undefined") return null;
  return window.__TPG_NATIVE_CAST__ || null;
}

/**
 * @param {object} [options]
 */
export function createFutureNativeProvider(options) {
  options = options || {};
  var bridge = detectNativeBridge();

  // If a native shell already injected a bridge implementing the provider
  // contract, wrap/adapt it. Otherwise stay foundation-only.
  if (
    bridge &&
    typeof bridge.connect === "function" &&
    typeof bridge.isAvailable === "function"
  ) {
    var emitter = createEmitter();
    var state = CastingConnectionState.DISCONNECTED;
    return {
      name: "future_native",
      getCapabilities: function () {
        return Object.assign(
          {
            id: "future_native",
            name: "Native App Cast",
            operationalOnWeb: false,
            requiresNativeApp: true,
            bridged: true,
          },
          (bridge.getCapabilities && bridge.getCapabilities()) || {}
        );
      },
      getCapabilityState: function () {
        if (bridge.getCapabilityState) return bridge.getCapabilityState();
        if (bridge.isAvailable && bridge.isAvailable()) {
          return CastingCapabilityState.AVAILABLE;
        }
        return CastingCapabilityState.REQUIRES_NATIVE_APP;
      },
      initialize: function () {
        return Promise.resolve(bridge.initialize && bridge.initialize()).then(
          function () {
            emitter.emit("availability", !!(bridge.isAvailable && bridge.isAvailable()));
          }
        );
      },
      isAvailable: function () {
        return !!(bridge.isAvailable && bridge.isAvailable());
      },
      isConnected: function () {
        return !!(bridge.isConnected && bridge.isConnected());
      },
      discoverDevices: function () {
        return bridge.discoverDevices
          ? bridge.discoverDevices()
          : Promise.resolve([]);
      },
      getDevices: function () {
        return bridge.getDevices ? bridge.getDevices() : [];
      },
      connect: function (deviceId) {
        state = CastingConnectionState.CONNECTING;
        emitter.emit("session", { type: "starting", deviceId: deviceId });
        return bridge.connect(deviceId).then(function () {
          state = CastingConnectionState.CONNECTED;
          emitter.emit("session", { type: "started" });
        });
      },
      disconnect: function () {
        return Promise.resolve(bridge.disconnect && bridge.disconnect()).then(
          function () {
            state = CastingConnectionState.DISCONNECTED;
            emitter.emit("session", { type: "ended", reason: "user" });
          }
        );
      },
      loadMedia: function (media) {
        return bridge.loadMedia ? bridge.loadMedia(media) : Promise.resolve();
      },
      play: function () {
        return bridge.play ? bridge.play() : Promise.resolve();
      },
      pause: function () {
        return bridge.pause ? bridge.pause() : Promise.resolve();
      },
      seek: function (t) {
        return bridge.seek ? bridge.seek(t) : Promise.resolve();
      },
      stop: function () {
        return bridge.stop ? bridge.stop() : Promise.resolve();
      },
      setVolume: function (v) {
        return bridge.setVolume ? bridge.setVolume(v) : Promise.resolve();
      },
      setMuted: function (m) {
        return bridge.setMuted ? bridge.setMuted(m) : Promise.resolve();
      },
      synchronizeState: function (snap) {
        return bridge.synchronizeState
          ? bridge.synchronizeState(snap)
          : Promise.resolve({ target: "local" });
      },
      getRemoteState: function () {
        return bridge.getRemoteState
          ? bridge.getRemoteState()
          : { target: "local", playing: false, currentTime: 0 };
      },
      getConnectionState: function () {
        return bridge.getConnectionState
          ? bridge.getConnectionState()
          : state;
      },
      getLastError: function () {
        return bridge.getLastError ? bridge.getLastError() : null;
      },
      on: emitter.on,
      off: emitter.off,
      destroy: function () {
        if (bridge.destroy) bridge.destroy();
        emitter.removeAllListeners();
      },
    };
  }

  var req = CastPlatformReceiverRequirements.future_native;
  return createReceiverRequiredProvider(
    {
      id: "future_native",
      displayName: "Native App Cast",
      capabilityState: CastingCapabilityState.REQUIRES_NATIVE_APP,
      reason: req.mechanism,
      receiverContract: {
        bridgeGlobal: "__TPG_NATIVE_CAST__",
        note: "Native shell injects bridge implementing CastingProvider contract.",
      },
    },
    options
  );
}

export default createFutureNativeProvider;
